// src/pages/AuthAction.jsx
//
// Custom handler for Firebase Auth email action links (password reset, email
// verification, email-change recovery), replacing the default
// <project-id>.firebaseapp.com/__/auth/action page.
//
// Two reasons this exists instead of using Firebase's hosted page:
//  1. Branding — a bare Google-hosted page makes the link feel illegitimate.
//  2. oobCode is single-use. Firebase's default page calls confirmPasswordReset/
//     applyActionCode automatically on load. Any automated fetch of the link
//     (corporate email link-scanners like Safe Links/Mimecast prefetching for
//     malware checks, a retried page load, etc.) burns the code before the real
//     user ever sees it, and they hit "expired or already used". verifyPasswordResetCode
//     and checkActionCode are read-only, so we use those on load; the code is
//     only consumed (confirmPasswordReset / applyActionCode) once the user
//     actually submits a form.
// See: https://firebase.google.com/docs/auth/custom-email-handler
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
} from 'firebase/auth';
import { auth } from '../firebase/firebase';

const inputStyle = {
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  lineHeight: '1.5',
  boxSizing: 'border-box',
  width: '100%',
};

const buttonStyle = {
  padding: '14px 24px',
  border: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
  boxSizing: 'border-box',
  display: 'inline-block',
  textAlign: 'center',
  textDecoration: 'none',
  background: 'var(--branding-app-primary)',
  color: '#fff',
};

const cardStyle = {
  background: '#f7f9fc',
  // clamp() scales padding down on narrow phones instead of a fixed 40px eating
  // most of the screen width, without needing a separate media-query breakpoint.
  padding: 'clamp(20px, 6vw, 40px)',
  borderRadius: '12px',
  boxShadow: '0 4px 30px rgba(0,0,0,0.05)',
  border: '1px solid var(--branding-app-primary)',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  width: '100%',
  maxWidth: '420px',
  textAlign: 'center',
};

function friendlyError(err) {
  const code = err?.code || '';
  if (code === 'auth/expired-action-code') {
    return "This link has expired. Request a new one and open it as soon as it arrives.";
  }
  if (code === 'auth/invalid-action-code') {
    return "This link has already been used or is invalid. Request a new one.";
  }
  if (code === 'auth/user-disabled') {
    return "This account has been disabled. Contact support.";
  }
  if (code === 'auth/weak-password') {
    return "Password is too weak. Must be at least 8 characters with one capital letter.";
  }
  return err?.message || "Something went wrong. Please request a new link.";
}

function Shell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#f7f9fc',
      fontFamily: 'sans-serif',
      boxSizing: 'border-box',
    }}>
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Small fixed floor + viewport-scaled amount: never crowds a small
        // phone screen, still centers comfortably on desktop.
        padding: 'clamp(16px, 5vw, 40px)',
        boxSizing: 'border-box',
        width: '100%',
      }}>
        <div style={cardStyle}>{children}</div>
      </div>
    </div>
  );
}

function ResetPasswordAction({ oobCode }) {
  const [status, setStatus] = useState('checking'); // checking | ready | submitting | done | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Read-only check — safe even if a scanner fetches this page first.
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setStatus('ready');
      })
      .catch((err) => {
        setError(friendlyError(err));
        setStatus('error');
      });
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one capital letter.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setStatus('submitting');
    try {
      // Only this call actually consumes the code.
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('done');
    } catch (err) {
      setError(friendlyError(err));
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return <p>Checking your reset link…</p>;
  }

  if (status === 'error') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Reset link invalid</h2>
        <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  if (status === 'done') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Password updated</h2>
        <p style={{ margin: 0 }}>You can now sign in with your new password.</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      <h2 style={{ margin: 0 }}>Reset your password</h2>
      <p style={{ margin: 0, color: '#64748b', wordBreak: 'break-word' }}>for {email}</p>
      {error && <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>}
      <input
        type="password"
        placeholder="New password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        style={inputStyle}
        autoFocus
      />
      <input
        type="password"
        placeholder="Confirm new password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
        style={inputStyle}
      />
      <button type="submit" style={buttonStyle} disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Updating…' : 'Update password'}
      </button>
    </form>
  );
}

function VerifyEmailAction({ oobCode }) {
  const [status, setStatus] = useState('checking'); // checking | ready | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    // Read-only check first so a prefetching scanner can't burn the code.
    checkActionCode(auth, oobCode)
      .then(() => setStatus('ready'))
      .catch((err) => {
        setError(friendlyError(err));
        setStatus('error');
      });
  }, [oobCode]);

  const handleConfirm = async () => {
    setStatus('checking');
    try {
      await applyActionCode(auth, oobCode);
      setStatus('done');
    } catch (err) {
      setError(friendlyError(err));
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return <p>Checking your verification link…</p>;
  }

  if (status === 'error') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Verification link invalid</h2>
        <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  if (status === 'done') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Email verified</h2>
        <p style={{ margin: 0 }}>You can now sign in.</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  return (
    <>
      <h2 style={{ margin: 0 }}>Verify your email</h2>
      <p style={{ margin: 0, color: '#64748b' }}>Tap below to confirm this email address for your CrewChain account.</p>
      <button type="button" style={buttonStyle} onClick={handleConfirm}>Verify email</button>
    </>
  );
}

function VerifyAndChangeEmailAction({ oobCode }) {
  const [status, setStatus] = useState('checking'); // checking | ready | done | error
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [previousEmail, setPreviousEmail] = useState('');

  useEffect(() => {
    // Read-only check first so a prefetching scanner can't burn the code.
    checkActionCode(auth, oobCode)
      .then((info) => {
        setNewEmail(info.data.email || '');
        setPreviousEmail(info.data.previousEmail || '');
        setStatus('ready');
      })
      .catch((err) => {
        setError(friendlyError(err));
        setStatus('error');
      });
  }, [oobCode]);

  const handleConfirm = async () => {
    setStatus('checking');
    try {
      await applyActionCode(auth, oobCode);
      setStatus('done');
    } catch (err) {
      setError(friendlyError(err));
      setStatus('error');
    }
  };

  if (status === 'checking') {
    return <p>Checking your link…</p>;
  }

  if (status === 'error') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Link invalid</h2>
        <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  if (status === 'done') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Email updated</h2>
        <p style={{ margin: 0 }}>Your account email is now {newEmail || 'updated'}.</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  return (
    <>
      <h2 style={{ margin: 0 }}>Confirm email change</h2>
      <p style={{ margin: 0, color: '#64748b', wordBreak: 'break-word' }}>
        {previousEmail && <>Change your CrewChain login email from <strong>{previousEmail}</strong> to </>}
        <strong>{newEmail}</strong>?
      </p>
      <button type="button" style={buttonStyle} onClick={handleConfirm}>Confirm change</button>
    </>
  );
}

function RecoverEmailAction({ oobCode }) {
  const [status, setStatus] = useState('working'); // working | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    // Recovering from an unsolicited email change is itself the sensitive
    // action here, but there's no follow-up step to gate behind a tap — apply
    // immediately, same as Firebase's own default handler.
    applyActionCode(auth, oobCode)
      .then(() => setStatus('done'))
      .catch((err) => {
        setError(friendlyError(err));
        setStatus('error');
      });
  }, [oobCode]);

  if (status === 'working') {
    return <p>Restoring your email address…</p>;
  }

  if (status === 'error') {
    return (
      <>
        <h2 style={{ margin: 0 }}>Link invalid</h2>
        <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </>
    );
  }

  return (
    <>
      <h2 style={{ margin: 0 }}>Email restored</h2>
      <p style={{ margin: 0 }}>Your original email address has been restored. If you didn't request this change, reset your password immediately.</p>
      <Link to="/" style={buttonStyle}>Back to sign in</Link>
    </>
  );
}

export default function AuthAction() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  if (!mode || !oobCode) {
    return (
      <Shell>
        <h2 style={{ margin: 0 }}>Invalid link</h2>
        <p style={{ margin: 0, color: '#64748b' }}>This link is missing required information. Please request a new one.</p>
        <Link to="/" style={buttonStyle}>Back to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell>
      {mode === 'resetPassword' && <ResetPasswordAction oobCode={oobCode} />}
      {mode === 'verifyEmail' && <VerifyEmailAction oobCode={oobCode} />}
      {mode === 'verifyAndChangeEmail' && <VerifyAndChangeEmailAction oobCode={oobCode} />}
      {mode === 'recoverEmail' && <RecoverEmailAction oobCode={oobCode} />}
      {!['resetPassword', 'verifyEmail', 'verifyAndChangeEmail', 'recoverEmail'].includes(mode) && (
        <>
          <h2 style={{ margin: 0 }}>Unsupported action</h2>
          <Link to="/" style={buttonStyle}>Back to sign in</Link>
        </>
      )}
    </Shell>
  );
}
